/**
 * Procedural SFX via the Web Audio API. We generate every cue from oscillators and
 * noise bursts at runtime — no audio files in the bundle, no decode latency, no licensing.
 *
 * Telegram on iOS suspends the AudioContext until a user gesture; we lazy-initialise
 * on the first call from a click handler. Subsequent calls just resume() and play.
 *
 * The mute flag lives in localStorage under 'durakapp:sound' and is read by the
 * useSettings store so the SideMenu toggle can flip it.
 */

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Klass = (window.AudioContext ?? (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!Klass) return null;
  ctx = new Klass();
  return ctx;
}

function isMuted(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem('durakapp:sound') === 'off';
}

/** Plays a single sine/triangle tone with a short ADSR envelope. */
function tone(opts: {
  freq: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  delay?: number;
}): void {
  if (isMuted()) return;
  const c = audioCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  const start = c.currentTime + (opts.delay ?? 0);
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, start);
  const peak = (opts.gain ?? 0.18);
  const attack = opts.attack ?? 0.005;
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(peak, start + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, start + opts.duration);
  osc.connect(env).connect(c.destination);
  osc.start(start);
  osc.stop(start + opts.duration + 0.05);
}

/** Short noise burst — useful for tap, swoosh, shuffle. */
function noise(opts: { duration: number; gain?: number; lowpass?: number; delay?: number }): void {
  if (isMuted()) return;
  const c = audioCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  const start = c.currentTime + (opts.delay ?? 0);
  const samples = Math.max(1, Math.floor(c.sampleRate * opts.duration));
  const buf = c.createBuffer(1, samples, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(opts.lowpass ?? 2200, start);
  const env = c.createGain();
  const peak = opts.gain ?? 0.12;
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(peak, start + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, start + opts.duration);
  src.connect(filter).connect(env).connect(c.destination);
  src.start(start);
  src.stop(start + opts.duration + 0.02);
}

export const sfx = {
  /** Tap on a card (light, very short). */
  tap(): void {
    tone({ freq: 1200, duration: 0.06, type: 'triangle', gain: 0.08, attack: 0.001 });
  },
  /** Card lands on the table. Two-stage swoosh + thud. */
  cardPlay(): void {
    noise({ duration: 0.08, gain: 0.08, lowpass: 1800 });
    tone({ freq: 220, duration: 0.12, type: 'sine', gain: 0.18, delay: 0.04 });
  },
  /** Defender takes — soft thud. */
  cardTake(): void {
    tone({ freq: 110, duration: 0.18, type: 'sine', gain: 0.18 });
    tone({ freq: 90, duration: 0.18, type: 'sine', gain: 0.10, delay: 0.04 });
  },
  /** "Бито" — round cleared. Two ascending dabs. */
  cardPass(): void {
    tone({ freq: 520, duration: 0.10, type: 'triangle', gain: 0.14 });
    tone({ freq: 780, duration: 0.10, type: 'triangle', gain: 0.14, delay: 0.07 });
  },
  /** Match starts — quick shuffle. */
  matchStart(): void {
    noise({ duration: 0.18, gain: 0.10, lowpass: 1600 });
    noise({ duration: 0.14, gain: 0.10, lowpass: 1600, delay: 0.1 });
    tone({ freq: 660, duration: 0.12, type: 'triangle', gain: 0.12, delay: 0.22 });
  },
  /** Victory — major triad with a flourish. */
  win(): void {
    tone({ freq: 523.25, duration: 0.5, type: 'triangle', gain: 0.18 });           // C5
    tone({ freq: 659.25, duration: 0.5, type: 'triangle', gain: 0.16, delay: 0.10 }); // E5
    tone({ freq: 783.99, duration: 0.55, type: 'triangle', gain: 0.18, delay: 0.20 }); // G5
    tone({ freq: 1046.5, duration: 0.6, type: 'triangle', gain: 0.16, delay: 0.32 });  // C6
  },
  /** Loss — minor triad descending. */
  lose(): void {
    tone({ freq: 392, duration: 0.45, type: 'sine', gain: 0.18 });           // G4
    tone({ freq: 311.13, duration: 0.45, type: 'sine', gain: 0.16, delay: 0.10 }); // D#4 / Eb4
    tone({ freq: 261.63, duration: 0.6, type: 'sine', gain: 0.18, delay: 0.20 }); // C4
  },
  /** Draw — single neutral note. */
  draw(): void {
    tone({ freq: 440, duration: 0.4, type: 'sine', gain: 0.14 });
  },
  /** Card sort cycle. */
  sort(): void {
    tone({ freq: 880, duration: 0.04, type: 'triangle', gain: 0.08 });
    tone({ freq: 1320, duration: 0.04, type: 'triangle', gain: 0.08, delay: 0.04 });
  },
};
